import {useEffect, useMemo, useState} from "react";
import {
    EBirdChecklistByFamily,
    EBirdMyDataSchema,
    EBirdObservationsByLocation,
    EBirdObservationsBySpecies,
    getChecklistByFamily,
    getFilteredObservations,
    getObservationsByLocation,
    getObservationsBySpecies
} from "ebird-mydata-reader";
import checklistWithImages from '../assets/data/checklist-with-images.json';
import * as localforage from "localforage";
import {getHotspotHabitatFeedbackSummary} from "../api/hotspot-habitat-feedback-summary";
import {getHotspotHabitatOverrideSummary} from "../api/hotspot-habitat-override-summary";
import {DEBUG_PERF, endPerfTimer, startPerfTimer} from "./perf";
import {
    CachedLocationEcoData,
    LOCATION_ECO_DATA_CACHE_KEY,
    LOCATION_ECO_DATA_CACHE_VERSION,
    CachedLocationRegionData,
    LOCATION_REGION_DATA_CACHE_KEY,
    LOCATION_REGION_DATA_CACHE_VERSION
} from "./birdDataCache";
import type {
    EnrichedObservationsByLocation,
    HotspotHabitatFeedbackSummary,
    HotspotHabitatOverride,
    LocationEcoregionData,
    LocationRegionData
} from "./types";

const checklistBaseWithImages = checklistWithImages as unknown as EBirdChecklistByFamily[];

export function useBirdData(rawData: any[]) {
    const [observationsBySpecies, setObservationsBySpecies] = useState<EBirdObservationsBySpecies[]>([]);
    const [observationsByLocation, setObservationsByLocation] = useState<EBirdObservationsByLocation[]>([]);
    const [ecoDataByLocationId, setEcoDataByLocationId] = useState<Record<string, LocationEcoregionData>>({});
    const [regionDataByLocationId, setRegionDataByLocationId] = useState<Record<string, LocationRegionData>>({});
    const [habitatFeedbackSummaryByLocationId, setHabitatFeedbackSummaryByLocationId] = useState<Record<string, HotspotHabitatFeedbackSummary>>({});
    const [habitatOverrideByLocationId, setHabitatOverrideByLocationId] = useState<Record<string, HotspotHabitatOverride>>({});
    const [checklist, setChecklist] = useState<EBirdChecklistByFamily[]>([]);
    const lifeList = useMemo((): EBirdMyDataSchema[] => {
        return rawData.length ? getFilteredObservations(rawData, 'life') : [];
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveLocationEcoData = async () => {
            if (!rawData.length) {
                setEcoDataByLocationId({});
                return;
            }

            const timer = startPerfTimer('birdData:loadEcoData');
            try {
                const cachedEcoData: CachedLocationEcoData | null = await localforage.getItem(LOCATION_ECO_DATA_CACHE_KEY);

                if (!isActive) return;

                setEcoDataByLocationId(
                    cachedEcoData?.version === LOCATION_ECO_DATA_CACHE_VERSION
                        ? cachedEcoData.ecoDataByLocationId ?? {}
                        : {}
                );
            } catch (error) {
                console.error(error);
                if (!isActive) return;

                setEcoDataByLocationId({});
            } finally {
                endPerfTimer(timer);
            }
        }

        void resolveLocationEcoData();

        return () => {
            isActive = false;
        }
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveLocationRegionData = async () => {
            if (!rawData.length) {
                setRegionDataByLocationId({});
                return;
            }

            const timer = startPerfTimer('birdData:loadRegionData');
            try {
                const cachedRegionData: CachedLocationRegionData | null = await localforage.getItem(LOCATION_REGION_DATA_CACHE_KEY);

                if (!isActive) return;

                setRegionDataByLocationId(
                    cachedRegionData?.version === LOCATION_REGION_DATA_CACHE_VERSION
                        ? cachedRegionData.regionDataByLocationId ?? {}
                        : {}
                );
            } catch (error) {
                console.error(error);
                if (!isActive) return;

                setRegionDataByLocationId({});
            } finally {
                endPerfTimer(timer);
            }
        }

        void resolveLocationRegionData();

        return () => {
            isActive = false;
        }
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveDerivedBirdData = async () => {
            if (!rawData.length) {
                setObservationsBySpecies([]);
                setObservationsByLocation([]);
                setChecklist([]);
                return;
            }

            const totalTimer = startPerfTimer('birdData:resolveDerivedBirdData');
            try {
                if (DEBUG_PERF) console.debug('birdData:derivedCacheBypassed');
                const speciesTimer = startPerfTimer('birdData:getObservationsBySpecies');
                const nextObservationsBySpecies = getObservationsBySpecies(rawData);
                endPerfTimer(speciesTimer);
                const locationTimer = startPerfTimer('birdData:getObservationsByLocation');
                const nextObservationsByLocation = getObservationsByLocation(rawData);
                endPerfTimer(locationTimer);
                const checklistTimer = startPerfTimer('birdData:getChecklistByFamily');
                const nextChecklist = nextObservationsBySpecies.length
                    ? getChecklistByFamily(nextObservationsBySpecies, checklistBaseWithImages)
                    : [];
                endPerfTimer(checklistTimer);

                if (!isActive) return;

                setObservationsBySpecies(nextObservationsBySpecies);
                setObservationsByLocation(nextObservationsByLocation);
                setChecklist(nextChecklist);
            } catch (error) {
                console.error(error);

                if (DEBUG_PERF) console.debug('birdData:derivedFallbackRebuild');
                const speciesTimer = startPerfTimer('birdData:getObservationsBySpecies:fallback');
                const nextObservationsBySpecies = getObservationsBySpecies(rawData);
                endPerfTimer(speciesTimer);
                const locationTimer = startPerfTimer('birdData:getObservationsByLocation:fallback');
                const nextObservationsByLocation = getObservationsByLocation(rawData);
                endPerfTimer(locationTimer);
                const checklistTimer = startPerfTimer('birdData:getChecklistByFamily:fallback');
                const nextChecklist = nextObservationsBySpecies.length
                    ? getChecklistByFamily(nextObservationsBySpecies, checklistBaseWithImages)
                    : [];
                endPerfTimer(checklistTimer);

                if (!isActive) return;

                setObservationsBySpecies(nextObservationsBySpecies);
                setObservationsByLocation(nextObservationsByLocation);
                setChecklist(nextChecklist);
            } finally {
                endPerfTimer(totalTimer);
            }
        }

        void resolveDerivedBirdData();

        return () => {
            isActive = false;
        }
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveHabitatOverrides = async () => {
            if (!observationsByLocation.length) {
                setHabitatOverrideByLocationId({});
                return;
            }

            const locationIds = Array.from(new Set(
                observationsByLocation
                    .map(locationGroup => locationGroup.locationId)
                    .filter(locationId => {
                        if (!locationId) return false;

                        return !ecoDataByLocationId[locationId]?.habitatOverride;
                    })
            ));

            if (!locationIds.length) {
                setHabitatOverrideByLocationId({});
                return;
            }

            const timer = startPerfTimer('birdData:loadHabitatOverrides');
            try {
                const response = await getHotspotHabitatOverrideSummary(locationIds);
                if (!isActive) return;

                setHabitatOverrideByLocationId(response.overridesByLocationId ?? {});
            } catch (error) {
                console.error(error);
                if (!isActive) return;

                setHabitatOverrideByLocationId({});
            } finally {
                endPerfTimer(timer);
            }
        };

        void resolveHabitatOverrides();

        return () => {
            isActive = false;
        };
    }, [ecoDataByLocationId, observationsByLocation]);

    useEffect(() => {
        let isActive = true;

        const resolveHabitatFeedbackSummary = async () => {
            if (!observationsByLocation.length) {
                setHabitatFeedbackSummaryByLocationId({});
                return;
            }

            const locationIds = Array.from(new Set(
                observationsByLocation
                    .map(locationGroup => locationGroup.locationId)
                    .filter(Boolean)
            ));

            if (!locationIds.length) {
                setHabitatFeedbackSummaryByLocationId({});
                return;
            }

            const timer = startPerfTimer('birdData:loadHabitatFeedbackSummary');
            try {
                const response = await getHotspotHabitatFeedbackSummary(locationIds);
                if (!isActive) return;

                setHabitatFeedbackSummaryByLocationId(response.summariesByLocationId ?? {});
            } catch (error) {
                console.error(error);
                if (!isActive) return;

                setHabitatFeedbackSummaryByLocationId({});
            } finally {
                endPerfTimer(timer);
            }
        };

        void resolveHabitatFeedbackSummary();

        return () => {
            isActive = false;
        };
    }, [observationsByLocation]);

    const activeObservationsByLocation = useMemo((): EBirdObservationsByLocation[] | EnrichedObservationsByLocation[] => {
        const timer = startPerfTimer('birdData:buildActiveObservationsByLocation');
        if (
            !Object.keys(ecoDataByLocationId).length
            && !Object.keys(regionDataByLocationId).length
            && !Object.keys(habitatOverrideByLocationId).length
            && !Object.keys(habitatFeedbackSummaryByLocationId).length
        ) {
            endPerfTimer(timer);
            return observationsByLocation;
        }

        const result = observationsByLocation.map(locationGroup => {
            const ecoData = ecoDataByLocationId[locationGroup.locationId];
            const regionData = regionDataByLocationId[locationGroup.locationId];
            const habitatOverride = ecoData?.habitatOverride ?? habitatOverrideByLocationId[locationGroup.locationId];
            const habitatFeedbackSummary = habitatFeedbackSummaryByLocationId[locationGroup.locationId];
            if (!ecoData && !regionData && !habitatOverride && !habitatFeedbackSummary) return locationGroup;

            return {
                ...locationGroup,
                ...(ecoData ? {ecoData} : {}),
                ...(regionData ? {regionData} : {}),
                ...(habitatOverride ? {habitatOverride} : {}),
                ...(habitatFeedbackSummary ? {habitatFeedbackSummary} : {})
            };
        });
        endPerfTimer(timer);
        return result;
    }, [ecoDataByLocationId, habitatFeedbackSummaryByLocationId, habitatOverrideByLocationId, observationsByLocation, regionDataByLocationId]);

    return {
        lifeList,
        observationsBySpecies,
        observationsByLocation: activeObservationsByLocation,
        checklist
    }
}
